import { useEffect, useState } from 'react';

import useOrganizationImpersonation from '../../hooks/useOrganizationImpersonation';
import { useOrganization } from '../../hooks/useOrganizations';
import { useAuthUser } from '../../hooks/useUser';
import { logError } from '../../rollbar';

// Token-authenticated Nova route, same auth model as /firebase-api/mirror-nav:
// the Firebase ID token goes in a Bearer header, no Nova session cookie.
const TEXTING_LINE_PATH = '/firebase-api/texting-line';

/** Nova's response for `GET /firebase-api/texting-line`. Nova decides eligibility. */
export type TextingLine = {
  show: boolean;
  number: string;
  display_number: string;
  sms_link: string;
  has_texted: boolean;
};

export type TextingLineState = { status: 'loading' } | { status: 'hidden' } | { status: 'ready'; line: TextingLine };

export function textingLineRequestUrl(baseUrl: string, schoolId: string): string {
  return `${baseUrl}${TEXTING_LINE_PATH}?school_id=${encodeURIComponent(schoolId)}`;
}

function parseTextingLine(body: unknown): TextingLine | null {
  if (!body || typeof body !== 'object') return null;
  const data = body as Record<string, unknown>;
  if (data.show !== true) return null;
  if (typeof data.number !== 'string' || !data.number) return null;
  if (typeof data.display_number !== 'string' || !data.display_number) return null;
  if (typeof data.sms_link !== 'string' || !data.sms_link.startsWith('sms:')) return null;
  return {
    show: true,
    number: data.number,
    display_number: data.display_number,
    sms_link: data.sms_link,
    has_texted: data.has_texted === true,
  };
}

/**
 * Reads the shared Beale texting line for the current program from Nova.
 *
 * Resolves to `hidden` whenever the card should not render: disabled, no
 * marketplace school id, no base URL, a non-OK response, a malformed body, or
 * `show: false`. Errors never throw into the dashboard.
 */
export function useTextingLine({ enabled }: { enabled: boolean }): TextingLineState {
  const organization = useOrganization();
  const { isImpersonatingOrganization, impersonatedOrganizationId } = useOrganizationImpersonation();
  const { token } = useAuthUser();
  const [state, setState] = useState<TextingLineState>({ status: 'loading' });

  // While an admin is switching into another program, the current org in Redux
  // can still be the previous one. Wait until it matches, so we never ask Nova
  // about the wrong school.
  const programPending =
    isImpersonatingOrganization && !!impersonatedOrganizationId && organization?.id !== impersonatedOrganizationId;
  const schoolId = organization?.marketplaceSchoolId ? String(organization.marketplaceSchoolId) : null;

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_WS_MARKETPLACE_APP_URL;

    if (!enabled || !schoolId || !baseUrl) {
      setState({ status: 'hidden' });
      return;
    }
    if (programPending || !token) {
      setState({ status: 'loading' });
      return;
    }

    const abortController = new AbortController();
    setState({ status: 'loading' });

    (async () => {
      try {
        const response = await fetch(textingLineRequestUrl(baseUrl, schoolId), {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;
        if (!response.ok) {
          setState({ status: 'hidden' });
          return;
        }
        const line = parseTextingLine(await response.json());
        if (abortController.signal.aborted) return;
        setState(line ? { status: 'ready', line } : { status: 'hidden' });
      } catch (error) {
        if (abortController.signal.aborted) return;
        logError('useTextingLine: failed to load texting line', error, { schoolId });
        setState({ status: 'hidden' });
      }
    })();

    return () => abortController.abort();
  }, [enabled, schoolId, programPending, token]);

  return state;
}
